import React, { useRef, useEffect, useState } from 'react';
import { Chart, DoughnutController, ArcElement, Tooltip, Legend } from 'chart.js';
import PropTypes from 'prop-types';
import ChartTooltip from './ChartTooltip';

Chart.register(DoughnutController, ArcElement, Tooltip, Legend);

const DoughnutChart = ({
  data,
  labels,
  colors,
  width = 200,
  height = 200,
  thickness = 70,
  showLegend = false,
  centerText = null,
  valueFormatter,
}) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const valueFormatterRef = useRef(valueFormatter);
  valueFormatterRef.current = valueFormatter;
  const [tooltip, setTooltip] = useState({
    visible: false,
    x: 0,
    y: 0,
    label: '',
    value: '',
    color: '',
  });

  // Track mouse position over the canvas
  useEffect(() => {
    const canvas = chartRef.current;
    if (!canvas) return;
    const handleMouseMove = (e) => {
      if (!tooltip.visible) return;
      setTooltip((prev) => ({
        ...prev,
        x: e.clientX, // Use viewport coordinates for fixed positioning
        y: e.clientY,
      }));
    };
    canvas.addEventListener('mousemove', handleMouseMove);
    return () => {
      canvas.removeEventListener('mousemove', handleMouseMove);
    };
  }, [tooltip.visible]);

  useEffect(() => {
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }
    const ctx = chartRef.current.getContext('2d');
    chartInstance.current = new Chart(ctx, {
      type: 'doughnut',
      data: {
        labels,
        datasets: [
          {
            data,
            backgroundColor: colors,
            borderWidth: 0,
          },
        ],
      },
      options: {
        cutout: `${thickness}%`,
        plugins: {
          legend: { 
            display: false, // We'll create a custom legend
          },
          tooltip: {
            enabled: false,
            external: (context) => {
              const tooltipModel = context.tooltip;
              if (tooltipModel.opacity === 0) {
                setTooltip((prev) => ({ ...prev, visible: false }));
                return;
              }
              if (tooltipModel.dataPoints && tooltipModel.dataPoints.length > 0) {
                const dp = tooltipModel.dataPoints[0];
                setTooltip((prev) => ({
                  ...prev,
                  visible: true,
                  label: dp.label,
                  value: valueFormatterRef.current ? valueFormatterRef.current(dp.raw) : dp.raw,
                  color: dp.element.options.backgroundColor,
                }));
              }
            },
          },
        },
        hover: {
          mode: 'nearest',
          intersect: true,
        },
      },
    });
    return () => {
      chartInstance.current && chartInstance.current.destroy();
    };
  }, [data, labels, colors, thickness, showLegend]);

  return (
    <div style={{position: 'relative', width, height}}>
      <canvas 
        ref={chartRef} 
        width={width} 
        height={height}
      />
      {centerText && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
          pointerEvents: 'none'
        }}>
          <div style={{
            fontSize: '28px',
            fontWeight: 'bold',
            color: 'var(--primary-color)',
            lineHeight: 1
          }}>
            {centerText.number}
          </div>
          <div style={{
            fontSize: '14px',
            color: 'var(--grey-64)',
            marginTop: '4px'
          }}>
            {centerText.label}
          </div>
        </div>
      )}
      {showLegend && (
        <div style={{
          position: 'absolute',
          right: '-140px',
          top: '50%',
          transform: 'translateY(-50%)',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          minWidth: '120px'
        }}>
          {labels.map((label, index) => (
            <div key={index} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <div style={{
                width: '14px',
                height: '14px',
                backgroundColor: colors[index],
                borderRadius: '4px',
                flexShrink: 0
              }} />
              <span style={{
                fontSize: '12px',
                color: 'var(--grey-64)',
                whiteSpace: 'nowrap'
              }}>
                {label}
              </span>
            </div>
          ))}
        </div>
      )}
      <ChartTooltip
        visible={tooltip.visible}
        x={tooltip.x}
        y={tooltip.y}
        content={
          <div style={{ display: 'flex', alignItems: 'center', minWidth: 90 }}>
            <span
              style={{
                display: 'inline-block',
                width: 14,
                height: 14,
                background: tooltip.color,
                borderRadius: 4,
                marginRight: 12,
              }}
            />
            <span style={{ color: 'var(--grey-64)', marginRight: 12 }}>
              {tooltip.label}
            </span>
            <span style={{ color: 'var(--primary-color)' }}>
              {tooltip.value}
            </span>
          </div>
        }
      />
    </div>
  );
};

DoughnutChart.propTypes = {
  /** Array of data values for the chart segments */
  data: PropTypes.arrayOf(PropTypes.number).isRequired,
  /** Array of labels for each segment */
  labels: PropTypes.arrayOf(PropTypes.string).isRequired,
  /** Array of colors for each segment */
  colors: PropTypes.arrayOf(PropTypes.string).isRequired,
  /** Width of the chart in pixels */
  width: PropTypes.number,
  /** Height of the chart in pixels */
  height: PropTypes.number,
  /** Thickness of the doughnut (0-100) where lower values make it thicker */
  thickness: PropTypes.number,
  /** Whether to show the legend */
  showLegend: PropTypes.bool,
  /** Center text to display in the doughnut hole */
  centerText: PropTypes.shape({
    number: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired
  }),
  valueFormatter: PropTypes.func
};

DoughnutChart.defaultProps = {
  width: 200,
  height: 200,
  thickness: 70,
  showLegend: false
};

export default DoughnutChart;
